import { FormEvent, useEffect, useRef, useState } from 'react'
import { Building2, Edit3, KeyRound, Plus, Power, Search, ShieldAlert, UsersRound, X } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import { EmptyState } from '../components/EmptyState'
import {
  chinaAdministrativeDivisions, emptyRegionSelection, formatRegion, industryOptions, resolveRegion,
  type RegionSelection,
} from '../reference-data/customerProfileOptions'
import { api } from '../lib/api'
import { can } from '../lib/permissions'
import type { Customer, CustomerPortalUser, Session } from '../types'

const typeLabels: Record<string, string> = {
  ENTERPRISE: '企业',
  GOVERNMENT: '政府机关',
  STATE_OWNED: '央国企',
  PUBLIC_INSTITUTION: '事业单位',
  BANK: '银行',
  INSURANCE: '保险机构',
  SECURITIES: '证券机构',
  FUND_MANAGEMENT: '基金管理机构',
  FINANCE: '其他金融机构',
  UNIVERSITY: '高校',
  RESEARCH_INSTITUTE: '科研院所',
  HOSPITAL: '医疗机构',
  FOUNDATION: '基金会',
  NON_PROFIT: '社会组织 / 非营利组织',
  MANUFACTURING: '制造企业',
  RETAIL: '零售企业',
  INTERNET: '互联网企业',
  TELECOM: '通信运营商',
  ENERGY: '能源企业',
  TRANSPORTATION: '交通运输',
  REAL_ESTATE: '房地产企业',
  PHARMACEUTICAL: '医药企业',
  FOREIGN_ENTERPRISE: '外资企业',
  SMALL_MEDIUM_ENTERPRISE: '中小企业',
  CHANNEL_PARTNER: '渠道伙伴',
}
const statusLabels: Record<string, string> = {
  PROSPECT: '潜在客户', ACTIVE: '合作中', SUSPENDED: '已暂停', INACTIVE: '已终止',
}
const emptyForm = {
  customerCode: '', name: '', status: 'ACTIVE', crmCustomerId: '', creditCode: '', type: 'ENTERPRISE',
  industry: '', region: '', contactName: '', contactPhone: '', salesOwner: '', commercialRisk: false,
}

export function CustomersPage() {
  const [searchParams] = useSearchParams()
  const [customers, setCustomers] = useState<Customer[]>([])
  const [keyword, setKeyword] = useState('')
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Customer>()
  const [form, setForm] = useState(emptyForm)
  const [regionSelection, setRegionSelection] = useState<RegionSelection>(emptyRegionSelection)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [session, setSession] = useState<Session>({ authenticated: true })
  const [portalCustomer, setPortalCustomer] = useState<Customer>()
  const [portalUsers, setPortalUsers] = useState<CustomerPortalUser[]>([])
  const [portalForm, setPortalForm] = useState({ username: '', password: '', displayName: '' })
  const [portalResetUser, setPortalResetUser] = useState<CustomerPortalUser>()
  const [portalResetPassword, setPortalResetPassword] = useState('')
  const [portalResetConfirmation, setPortalResetConfirmation] = useState('')
  const [portalMessage, setPortalMessage] = useState('')
  const [portalLoading, setPortalLoading] = useState(false)
  const [portalSaving, setPortalSaving] = useState(false)
  const consumedStartAction = useRef(false)
  const selectedProvince = chinaAdministrativeDivisions.find(item => item.code === regionSelection.provinceCode)
  const selectedCity = selectedProvince?.children.find(item => item.code === regionSelection.cityCode)
  const regionSelectionIncomplete = Boolean(regionSelection.provinceCode) && !regionSelection.districtCode

  const load = (query = '') => api.customers(query).then(result => setCustomers(result.content)).catch(value => setError(value.message))
  useEffect(() => { api.session().then(setSession).catch(() => undefined); load() }, [])
  const canManage = can(session, 'CUSTOMER_MANAGE')

  useEffect(() => {
    if (consumedStartAction.current || searchParams.get('start') !== 'new' || !canManage) return
    consumedStartAction.current = true
    openCreate()
  }, [canManage, searchParams])

  function openCreate() {
    setEditing(undefined)
    setForm(emptyForm)
    setRegionSelection(emptyRegionSelection)
    setOpen(true)
  }

  function openEdit(customer: Customer) {
    const resolvedRegion = resolveRegion(customer.region)
    setEditing(customer)
    setRegionSelection(resolvedRegion)
    setForm({
      customerCode: customer.customerCode,
      name: customer.name,
      status: customer.status,
      crmCustomerId: customer.crmCustomerId || '',
      creditCode: customer.creditCode || '',
      type: customer.type,
      industry: customer.industry || '',
      region: formatRegion(resolvedRegion) || customer.region || '',
      contactName: customer.contactName || '',
      contactPhone: customer.contactPhone || '',
      salesOwner: customer.salesOwner || '',
      commercialRisk: customer.commercialRisk,
    })
    setOpen(true)
  }

  function selectProvince(provinceCode: string) {
    setRegionSelection({ provinceCode, cityCode: '', districtCode: '' })
    setForm(current => ({ ...current, region: '' }))
  }

  function selectCity(cityCode: string) {
    setRegionSelection(current => ({ ...current, cityCode, districtCode: '' }))
    setForm(current => ({ ...current, region: '' }))
  }

  function selectDistrict(districtCode: string) {
    const selection = { ...regionSelection, districtCode }
    setRegionSelection(selection)
    setForm(current => ({ ...current, region: formatRegion(selection) }))
  }

  async function openPortalUsers(customer: Customer) {
    setError('')
    setPortalCustomer(customer)
    setPortalUsers([])
    setPortalForm({ username: '', password: '', displayName: '' })
    setPortalResetUser(undefined)
    setPortalMessage('')
    setPortalLoading(true)
    try { setPortalUsers(await api.customerPortalUsers(customer.id)) }
    catch (value) { setError(value instanceof Error ? value.message : '客户门户账号加载失败') }
    finally { setPortalLoading(false) }
  }

  async function createPortalUser(event: FormEvent) {
    event.preventDefault()
    if (!portalCustomer) return
    const validationError = validatePortalAccount(portalForm)
    if (validationError) { setError(validationError); return }
    setPortalSaving(true); setError('')
    try {
      const created = await api.createCustomerPortalUser(portalCustomer.id, portalForm)
      setPortalUsers(current => [...current, created])
      setPortalForm({ username: '', password: '', displayName: '' })
    } catch (value) { setError(value instanceof Error ? value.message : '门户账号创建失败') }
    finally { setPortalSaving(false) }
  }

  async function togglePortalUser(user: CustomerPortalUser) {
    if (!portalCustomer) return
    setPortalSaving(true); setError('')
    try {
      const updated = await api.updateCustomerPortalUser(portalCustomer.id, user.id, { displayName: user.displayName, enabled: !user.enabled })
      setPortalUsers(current => current.map(value => value.id === updated.id ? updated : value))
    } catch (value) { setError(value instanceof Error ? value.message : '门户账号状态更新失败') }
    finally { setPortalSaving(false) }
  }

  function openPortalPasswordReset(user: CustomerPortalUser) {
    setPortalResetUser(user)
    setPortalResetPassword('')
    setPortalResetConfirmation('')
    setPortalMessage('')
    setError('')
  }

  async function resetPortalUserPassword(event: FormEvent) {
    event.preventDefault()
    if (!portalCustomer || !portalResetUser) return
    if (portalResetPassword.length < 8) { setError('门户密码长度至少需要 8 位'); return }
    if (portalResetPassword !== portalResetConfirmation) { setError('两次输入的新密码不一致'); return }
    setPortalSaving(true); setError('')
    try {
      const updated = await api.updateCustomerPortalUser(portalCustomer.id, portalResetUser.id, {
        password: portalResetPassword, displayName: portalResetUser.displayName, enabled: portalResetUser.enabled,
      })
      setPortalUsers(current => current.map(value => value.id === updated.id ? updated : value))
      setPortalResetUser(undefined)
      setPortalResetPassword('')
      setPortalResetConfirmation('')
      setPortalMessage(`账号 ${updated.username} 的门户密码已重置，原有登录会话已失效。`)
    } catch (value) { setError(value instanceof Error ? value.message : '门户密码重置失败') }
    finally { setPortalSaving(false) }
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    setSaving(true)
    setError('')
    try {
      const payload = form as Omit<Customer, 'id' | 'createdAt'>
      if (editing) await api.updateCustomer(editing.id, payload)
      else await api.createCustomer(payload)
      setOpen(false)
      setEditing(undefined)
      setForm(emptyForm)
      await load(keyword)
    } catch (value) {
      setError(value instanceof Error ? value.message : '保存失败')
    } finally {
      setSaving(false)
    }
  }

  return <div>
    <div className="page-lead">
      <div><h2>客户资产</h2><p>统一维护客户主数据、CRM 映射、负责人、商业风险与客户门户账号。</p></div>
      {canManage && <button className="button button--primary" onClick={openCreate}><Plus size={17} />新建客户</button>}
    </div>
    <div className="filter-bar">
      <div className="search-box"><Search size={17} /><input value={keyword} onChange={event => setKeyword(event.target.value)} onKeyDown={event => event.key === 'Enter' && load(keyword)} placeholder="搜索客户编码、名称或 CRM ID" /></div>
      <span>共 {customers.length} 个客户</span>
    </div>
    {error && !portalCustomer && <div className="error-banner">{error}</div>}
    <section className="panel table-panel">{customers.length ? <div className="table-wrap"><table className="customers-table">
      <thead><tr><th>客户名称</th><th>客户状态</th><th>客户编码</th><th>客户负责人</th><th>商业风险</th><th>客户类型</th><th>联系人</th><th>门户账号</th><th className="align-right">操作</th></tr></thead>
      <tbody>{customers.map(item => <tr key={item.id}>
        <td><div className="entity-cell"><span><Building2 size={17} /></span><Link className="customer-case-link" to={`/customer-cases/${item.id}`}><b>{item.name}</b></Link></div></td>
        <td><span className={`customer-status customer-status--${item.status.toLowerCase()}`}>{statusLabels[item.status] || item.status}</span></td>
        <td><b className="cell-main mono">{item.customerCode}</b></td>
        <td><b className="cell-main">{item.salesOwner || '—'}</b><small>{item.industry || '未设置行业'} · {item.region || '未设置区域'}</small></td>
        <td>{item.commercialRisk ? <span className="risk-flag"><ShieldAlert size={13} />有风险</span> : <span className="risk-clear">无风险</span>}</td>
        <td><span className="type-tag">{typeLabels[item.type] || item.type}</span></td>
        <td><b className="cell-main">{item.contactName || '—'}</b><small>{item.contactPhone || '未录入电话'}</small></td>
        <td>{canManage ? <button className="button button--small button--ghost portal-account-query" onClick={() => void openPortalUsers(item)}><UsersRound size={14} />查看账号</button> : <small className="portal-account-permission">需客户管理权限</small>}</td>
        <td><div className="row-actions">{canManage && <button className="icon-button" title="编辑客户" aria-label={`编辑客户 ${item.name}`} onClick={() => openEdit(item)}><Edit3 size={16} /></button>}</div></td>
      </tr>)}</tbody>
    </table></div> : <EmptyState title="还没有客户" detail="创建客户后即可提交 License 申请" />}</section>

    {open && <div className="modal-layer"><div className="modal customer-modal">
      <div className="modal-heading"><div><h3>{editing ? '编辑客户' : '新建客户'}</h3></div><button className="icon-button" onClick={() => setOpen(false)} aria-label="关闭"><X size={19} /></button></div>
      <form onSubmit={submit} className="form-grid">
        <label>客户编码<input className="mono" value={form.customerCode} onChange={event => setForm({ ...form, customerCode: event.target.value })} maxLength={40} required placeholder="如：CUST-000123" /></label>
        <label>客户状态<select value={form.status} onChange={event => setForm({ ...form, status: event.target.value })}>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label className="span-2">客户名称<input value={form.name} onChange={event => setForm({ ...form, name: event.target.value })} maxLength={160} required placeholder="请输入合同主体全称" /></label>
        <label>客户负责人<input value={form.salesOwner} onChange={event => setForm({ ...form, salesOwner: event.target.value })} maxLength={80} /></label>
        <label>客户类型<select value={form.type} onChange={event => setForm({ ...form, type: event.target.value })}>{Object.entries(typeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label>统一社会信用代码<input value={form.creditCode} onChange={event => setForm({ ...form, creditCode: event.target.value })} maxLength={32} /></label>
        <label>所属行业<select value={form.industry} onChange={event => setForm({ ...form, industry: event.target.value })}><option value="">请选择所属行业</option>{form.industry && !industryOptions.some(option => option === form.industry) && <option value={form.industry}>{form.industry}（历史值）</option>}{industryOptions.map(industry => <option key={industry} value={industry}>{industry}</option>)}</select><small>按照国民经济行业门类选择，便于客户统计和授权分析。</small></label>
        <fieldset className="customer-region-field span-2"><legend>区域（省 / 市 / 区县）</legend><div className="customer-region-selects">
          <label>省 / 自治区 / 直辖市<select aria-label="省/自治区/直辖市" value={regionSelection.provinceCode} onChange={event => selectProvince(event.target.value)}><option value="">请选择省级行政区</option>{chinaAdministrativeDivisions.map(province => <option key={province.code} value={province.code}>{province.name}</option>)}</select></label>
          <label>市<select aria-label="市" value={regionSelection.cityCode} onChange={event => selectCity(event.target.value)} disabled={!selectedProvince}><option value="">请选择城市</option>{selectedProvince?.children.map(city => <option key={city.code} value={city.code}>{city.name}</option>)}</select></label>
          <label>区 / 县<select aria-label="区/县" value={regionSelection.districtCode} onChange={event => selectDistrict(event.target.value)} disabled={!selectedCity}><option value="">请选择区县</option>{selectedCity?.children.map(district => <option key={district.code} value={district.code}>{district.name}</option>)}</select></label>
        </div><small className={regionSelectionIncomplete ? 'customer-region-hint customer-region-hint--warning' : 'customer-region-hint'}>{regionSelectionIncomplete ? '请继续选择到区 / 县后再保存。' : form.region ? `已选择：${form.region}` : '区域可选；选择后将以完整省、市、区县路径保存。'}</small></fieldset>
        <label>联系人<input value={form.contactName} onChange={event => setForm({ ...form, contactName: event.target.value })} maxLength={80} /></label>
        <label>联系电话<input value={form.contactPhone} onChange={event => setForm({ ...form, contactPhone: event.target.value })} maxLength={40} /></label>
        <label className="switch-row span-2"><input type="checkbox" checked={form.commercialRisk} onChange={event => setForm({ ...form, commercialRisk: event.target.checked })} /><span><b>商业风险标记</b><small>存在回款、合同、信用或交付风险时开启</small></span></label>
        {error && <div className="form-error span-2">{error}</div>}
        <div className="modal-actions span-2"><button type="button" className="button button--ghost" onClick={() => setOpen(false)}>取消</button><button className="button button--primary" disabled={saving || regionSelectionIncomplete}>{saving ? '正在保存...' : '保存客户'}</button></div>
      </form>
    </div></div>}

    {portalCustomer && <div className="modal-layer"><div className="modal portal-user-modal">
      <div className="modal-heading"><div><small>CUSTOMER SELF SERVICE</small><h3>{portalCustomer.name} · 门户账号</h3></div><button className="icon-button" onClick={() => setPortalCustomer(undefined)} aria-label="关闭"><X size={19} /></button></div>
      <div className="portal-user-content"><p className="portal-user-note">门户账号与内部五岗位账号隔离，只能查看该客户的权益与激活并提交服务请求。停用或密码重置会立即撤销该账号的已登录会话。</p>{error && <div className="form-error">{error}</div>}{portalMessage && <div className="success-banner">{portalMessage}</div>}
        <div className="portal-user-list">{portalLoading ? <div className="portal-user-empty">正在加载客户门户账号...</div> : portalUsers.length ? portalUsers.map(user => <article key={user.id}><span><KeyRound size={16} /></span><div><b>{user.displayName}</b><small>{user.username} · {user.enabled ? '已启用' : '已停用'}</small></div><div className="portal-user-actions"><button className="button button--small button--ghost" disabled={portalSaving} onClick={() => openPortalPasswordReset(user)}><KeyRound size={13} />重置密码</button><button className="button button--small button--ghost" disabled={portalSaving} onClick={() => void togglePortalUser(user)}><Power size={13} />{user.enabled ? '停用' : '启用'}</button></div></article>) : <div className="portal-user-empty">未找到该客户的独立门户账号。客户联系人和内部管理账号不会自动作为门户账号显示。</div>}</div>
        <form className="portal-user-form" onSubmit={createPortalUser}><h4>创建门户联系人账号</h4><label>姓名<input value={portalForm.displayName} onChange={event => setPortalForm(current => ({ ...current, displayName: event.target.value }))} maxLength={80} required placeholder="例如：王经理" /></label><label>登录账号<input className="mono" value={portalForm.username} onChange={event => setPortalForm(current => ({ ...current, username: event.target.value }))} maxLength={80} required placeholder="例如：cust.acme.wang" /></label><label>初始密码<input type="password" value={portalForm.password} onChange={event => setPortalForm(current => ({ ...current, password: event.target.value }))} minLength={8} maxLength={256} required placeholder="至少 8 位" /></label><button className="button button--primary" disabled={portalSaving}><Plus size={15} />{portalSaving ? '正在保存...' : '创建门户账号'}</button></form>
      </div>
    </div></div>}

    {portalResetUser && portalCustomer && <div className="modal-layer"><div className="modal portal-reset-modal" role="dialog" aria-modal="true" aria-labelledby="portal-reset-title">
      <div className="modal-heading"><div><small>CUSTOMER SELF SERVICE</small><h3 id="portal-reset-title">重置门户密码</h3></div><button className="icon-button" type="button" onClick={() => setPortalResetUser(undefined)} aria-label="关闭"><X size={19} /></button></div>
      <form className="portal-reset-form" onSubmit={resetPortalUserPassword}><div className="modal-context"><KeyRound size={18} /><div><b>{portalResetUser.displayName} · {portalResetUser.username}</b><small>重置后该账号的所有已登录会话都会失效，需要使用新密码重新登录。</small></div></div><label>新密码<input type="password" value={portalResetPassword} onChange={event => setPortalResetPassword(event.target.value)} minLength={8} maxLength={256} autoFocus autoComplete="new-password" required /><small>门户密码长度至少需要 8 位。</small></label><label>确认新密码<input type="password" value={portalResetConfirmation} onChange={event => setPortalResetConfirmation(event.target.value)} minLength={8} maxLength={256} autoComplete="new-password" required /></label>{error && <div className="form-error">{error}</div>}<div className="modal-actions"><button type="button" className="button button--ghost" onClick={() => setPortalResetUser(undefined)} disabled={portalSaving}>取消</button><button type="submit" className="button button--primary" disabled={portalSaving}>{portalSaving ? '正在重置...' : '确认重置密码'}</button></div></form>
    </div></div>}
  </div>
}

function validatePortalAccount(account: { username: string, password: string, displayName: string }) {
  const username = account.username.trim()
  if (!account.displayName.trim() || !username) return '请填写姓名和登录账号'
  if (account.password.length < 8) return '门户密码长度至少需要 8 位'
  return ''
}
